/*
 * Copyright (c) 2025 BuyerView.ai
 * All rights reserved.
 * Unauthorized copying prohibited.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */


import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { googleAnalytics } from './services/googleAnalytics';

// Minimal imports for fastest initial load
// eslint-disable-next-line @typescript-eslint/no-unused-vars, react-refresh/only-export-components
const App = React.lazy(() => import('./App.tsx'));

// Defer expensive operations
const deferredOperations = {
  environmentValidation: () => import('./utils/envValidation'),
  storageSetup: () => import('./lib/storage-setup'),
  performanceMonitor: () => import('./services/performanceMonitor')
};

// Immediate render with minimal bundle
const root = createRoot(document.getElementById('root')!);

// Show loading screen immediately
root.render(
  <StrictMode>
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4ECDC4] mx-auto mb-4"></div>
        <p className="text-gray-600">Loading BuyerView.ai...</p>
      </div>
    </div>
  </StrictMode>
);

// Optimized async initialization - load only critical path
async function initializeApp() {
  try {
    // Set app start time right before actual work begins
    const appStartTime = performance.now();
    (window as any).__APP_START_TIME = appStartTime;

    // Load only the essential App component first
    const AppComponent = await import('./App.tsx');

    // Render app immediately - don't wait for other services
    root.render(
      <StrictMode>
        <AppComponent.default />
      </StrictMode>
    );

    // Mark when app component rendering is complete
    (window as any).__APP_RENDER_COMPLETE = performance.now();

    const loadTime = performance.now() - appStartTime;

    // Store deferred initialization time globally for monitoring
    (window as any).__DEFERRED_INIT_TIME = loadTime;

    // Background initialization - don't block UI
    setTimeout(async () => {
      try {
        // Load services in background after UI is ready
        const [perfMon, envValidation, storage] = await Promise.allSettled([
          deferredOperations.performanceMonitor(),
          import.meta.env.PROD ? deferredOperations.environmentValidation() : Promise.resolve(null),
          deferredOperations.storageSetup()
        ]);

        // Setup background services
        if (perfMon.status === 'fulfilled') {
          perfMon.value.performanceMonitor?.startMeasure?.('background_services');
        }

        if (storage.status === 'fulfilled') {
          storage.value.setupStorage?.().catch(() => {/* Silent fail */});
        }

        if (envValidation.status === 'fulfilled' && envValidation.value) {
          envValidation.value.initializeEnvironmentValidation?.();
        }

      } catch (bgError) {
        // Background services failed - app still works
      }
    }, 100); // Small delay to let UI settle

  } catch (error) {
    // Fallback error screen
    root.render(
      <StrictMode>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center text-red-600">
            <h1>Loading Error</h1>
            <p>Please refresh the page</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
            >
              Retry
            </button>
          </div>
        </div>
      </StrictMode>
    );
  }
}

// Start initialization immediately but don't block
initializeApp();